import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { dataGridColumn } from "../../../../../components/dataGridColumn";
import { useWorkflowById } from "../../../../../reactQuery/workflows";
import { currentUserId, isAdmin } from "../../../../../utils/constants";
import { manageColumnOrder } from "../../../../../services/apiCards";
import { objectToFormData } from "../../../../../utils";
import {
  setAssigneeIds,
  setMilestoneIds,
  setWorkflowIds,
} from "../../../../../redux/ListViewColumnHeaderFilterSlice";
import useToastPromise from "../../../../../hooks/useToast";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../reactQuery/response";
import {
  buildListViewRows,
  filterBoardCards,
  getUniqueAssignees,
  getUniqueMilestones,
  mergeColumnVisibility,
  reorderColumns,
} from "../listViewUtils";

export function useBoardListViewState({ workflowId, boardName, cards = [] }) {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { id } = useBoardIdByKey();
  const { showToastPromise } = useToastPromise();
  const { data: workflowsQuery } = useWorkflowById(workflowId, id);
  const { mutate } = useMutation({
    mutationFn: async (data) => showToastPromise(manageColumnOrder, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boardName", id],
      });
    },
    onError: (error) => {
      console.error("column sequence not updated", error);
    },
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [expandcardId, setExpandCardId] = useState(null);
  const [subtaskCards, setSubtaskCards] = useState([]);
  const [isSubtaskLoading, setIsSubtaskLoading] = useState(false);
  const [openCreateCard, setOpenCreateCard] = useState(false);

  const epicSearch = useSelector((state) => state.epic.searchEpic);
  const searchText = useSelector((state) => state.search.searchText);
  const deferredSearchText = useDeferredValue(searchText);

  const boardPermission = boardName?.board_permission;
  const currentUserIdValue = currentUserId();
  const canManageBoard =
    isAdmin() ||
    boardName?.owner_user_id == currentUserIdValue ||
    boardPermission?.is_admin;

  const havePermissionToEdit = useCallback(() => canManageBoard, [canManageBoard]);

  const baseColumns = useMemo(
    () => dataGridColumn(boardName, havePermissionToEdit, boardPermission),
    [boardName, boardPermission, havePermissionToEdit]
  );

  const [columns, setColumns] = useState(() =>
    reorderColumns(baseColumns, boardName?.card_column_sequence)
  );

  useEffect(() => {
    const nextColumns = reorderColumns(baseColumns, boardName?.card_column_sequence);
    setColumns((previousColumns) =>
      mergeColumnVisibility(nextColumns, previousColumns)
    );
  }, [baseColumns, boardName?.card_column_sequence]);

  const workflowSteps = useMemo(
    () => getResponseData(workflowsQuery, []),
    [workflowsQuery]
  );

  const filterUser = searchParams.get("search")?.split(",").map(Number) || [];
  const noMilestoneSearch = searchParams.get("noMilestone");
  const cardId = searchParams.get("card_id");
  const statusSearch = searchParams.get("workflow_Step");
  const estimated = searchParams.get("estimated");
  const columnHeaderStatus = searchParams.get("statusFilter")?.split(",") || [];
  const columnHeaderMilestone =
    searchParams.get("columnMilestone")?.split(",") || [];
  const columnHeaderAssignee =
    searchParams.get("assigneeFilter")?.split(",") || [];

  useEffect(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    let hasChanges = false;

    if (filterUser.length && nextSearchParams.has("assigneeFilter")) {
      nextSearchParams.delete("assigneeFilter");
      hasChanges = true;
    }

    if (epicSearch?.length && nextSearchParams.has("columnMilestone")) {
      nextSearchParams.delete("columnMilestone");
      hasChanges = true;
    }

    if (statusSearch && nextSearchParams.has("statusFilter")) {
      nextSearchParams.delete("statusFilter");
      hasChanges = true;
    }

    if (hasChanges) {
      setSearchParams(nextSearchParams);
    }
  }, [columnHeaderAssignee.length, epicSearch, filterUser.length, searchParams, setSearchParams, statusSearch]);

  const filteredCards = useMemo(
    () =>
      filterBoardCards(cards, {
        assigneeSearch: filterUser,
        epicSearch,
        noMilestoneSearch,
        searchText: deferredSearchText,
        statusSearch,
        estimated,
        columnHeaderStatus,
        columnHeaderMilestone,
        columnHeaderAssignee,
      }),
    [
      cards,
      columnHeaderAssignee,
      columnHeaderMilestone,
      columnHeaderStatus,
      deferredSearchText,
      estimated,
      epicSearch,
      filterUser,
      noMilestoneSearch,
      statusSearch,
    ]
  );

  const boardCards = useMemo(
    () =>
      filteredCards.map((item) =>
        item?.card?.card_id === expandcardId ? { ...item, subRow: subtaskCards } : item
      ),
    [expandcardId, filteredCards, subtaskCards]
  );

  const rows = useMemo(() => buildListViewRows(boardCards), [boardCards]);

  useEffect(() => {
    const parsedStatusSearch = Number(statusSearch);
    const ids = parsedStatusSearch
      ? [parsedStatusSearch]
      : [...new Set(cards.map((item) => item?.card?.workflow_step_id))].filter(Boolean);

    if (ids.length) {
      dispatch(
        setWorkflowIds({
          stepIds: ids,
          workflowId: boardName?.workflow_id,
        })
      );
    }
  }, [boardName?.workflow_id, cards, dispatch, statusSearch]);

  useEffect(() => {
    const ids = getUniqueMilestones(cards, epicSearch);

    if (ids.length) {
      dispatch(setMilestoneIds(ids));
    }
  }, [cards, dispatch, epicSearch]);

  useEffect(() => {
    const ids = getUniqueAssignees(cards, filterUser[0]);

    if (ids.length) {
      dispatch(setAssigneeIds(ids));
    }
  }, [cards, dispatch, filterUser]);

  const handleExpandedData = useCallback((cardIdValue) => {
    setExpandCardId(cardIdValue);
  }, []);

  const handleSelectColumn = useCallback((columnId) => {
    setColumns((previousColumns) =>
      previousColumns.map((column) =>
        column.id === columnId
          ? { ...column, isHidden: !column.isHidden }
          : column
      )
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setColumns((previousColumns) => {
      const shouldShowAll = previousColumns.every((column) =>
        ["cardId", "title", "assigned_to_user_name"].includes(column.id)
          ? true
          : column.isHidden
      );

      return previousColumns.map((column) =>
        ["cardId", "title", "assigned_to_user_name"].includes(column.id)
          ? column
          : { ...column, isHidden: !shouldShowAll ? true : false }
      );
    });
  }, []);

  const handleColumOrder = useCallback(
    (nextColumns) => {
      const newArrangement = {};

      nextColumns.forEach((column, index) => {
        newArrangement[column.id] = index;
      });

      mutate(
        objectToFormData({
          field_sequence: JSON.stringify(newArrangement),
        })
      );
    },
    [mutate]
  );

  return {
    anchorEl,
    boardCards,
    cardId,
    columns,
    expandcardId,
    handleColumOrder,
    handleExpandedData,
    handleSelectAll,
    handleSelectColumn,
    isSubtaskLoading,
    isMilestoneSelected: Boolean(searchParams.get("milestone")),
    open: Boolean(anchorEl),
    openCreateCard,
    rows,
    setAnchorEl,
    setIsSubtaskLoading,
    setOpenCreateCard,
    setSubtaskCards,
    workflowSteps,
  };
}
